import React, { useEffect, useState } from "react";
import {
  Tabs,
  Typography,
  Tooltip,
  message,
  Tag,
  Modal,
  Switch,
  Input,
} from "antd";
import { DndContext, useDroppable, useDraggable } from "@dnd-kit/core";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useSystemStatusStore from "./Store/index";
import SkeletonLoader from "./SkeletonLoader";

const { TabPane } = Tabs;
const { Title } = Typography;

const OTSManagement = () => {
  const {
    couriers,
    systemStatusCodes,
    statusMappings,
    unmappedKeys,
    fetchCouriers,
    fetchSystemStatusCodes,
    fetchMappings,
    postMapping,
    loading,
    error,
    updatePollers,
  } = useSystemStatusStore();

  const [activeUserFriendlyKey, setActiveUserFriendlyKey] = useState("");
  const [activeCourier, setActiveCourier] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState("");
  const [editingCode, setEditingCode] = useState(null);
  const [pollTime, setPollTime] = useState("");
  const [endPoll, setEndPoll] = useState(false);

  const handleEditTag = (field, code) => {
    setEditingField(field);
    setEditingCode(code);
    if (field === "pollTime") {
      setPollTime(code.next_poll_at || "");
    } else if (field === "endPoll") {
      setEndPoll(code.end_poll || false);
    }
    setIsModalOpen(true);
  };

  const handleSaveChanges = async () => {
    if (!editingCode) return;

    try {
      await updatePollers({
        tenant_id: "traya",
        body: {
          nextPoleAt:
            editingField === "pollTime"
              ? parseInt(pollTime, 10)
              : editingCode.next_poll_at,
          endPoll: editingField === "endPoll" ? endPoll : editingCode.end_poll,
          systemStatusCodeId: editingCode.id,
        },
      });
      setIsModalOpen(false);
      const tenantId = "traya";
      fetchCouriers(tenantId);
      fetchSystemStatusCodes(tenantId);
    } catch (error) {
      console.error("Failed to update polling details.");
    }
  };

  useEffect(() => {
    const tenantId = "traya";
    fetchCouriers(tenantId);
    fetchSystemStatusCodes(tenantId);
  }, [fetchCouriers, fetchSystemStatusCodes]);

  useEffect(() => {
    if (activeCourier) {
      const tenantId = "traya";
      fetchMappings({
        tenantId,
        courier: activeCourier.courier_partner_code,
        isMapped: true,
      });
      fetchMappings({
        tenantId,
        courier: activeCourier.courier_partner_code,
        isMapped: false,
      });
    }
  }, [activeCourier, fetchMappings]);

  useEffect(() => {
    if (couriers.length > 0 && !activeCourier) {
      setActiveCourier(couriers[0]);
    }
  }, [couriers, activeCourier]);

  useEffect(() => {
    if (systemStatusCodes.length > 0 && !activeUserFriendlyKey) {
      setActiveUserFriendlyKey(systemStatusCodes[0].system_status_code);
    }
  }, [systemStatusCodes, activeUserFriendlyKey]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over) return;

    const draggedKey = active.id;

    if (over.id === `mapped-${activeUserFriendlyKey}`) {
      const systemStatusCode = systemStatusCodes.find(
        (code) => code.system_status_code === activeUserFriendlyKey
      );

      if (systemStatusCode) {
        try {
          await postMapping({
            tenant_id: "traya",
            body: {
              cpCode: activeCourier.courier_partner_code,
              cpStatusCode: draggedKey,
              systemStatusCodeId: systemStatusCode.id,
            },
          });
          message.success("Mapping successful!");

          const tenantId = "traya";
          fetchMappings({
            tenantId,
            courier: activeCourier.courier_partner_code,
            isMapped: true,
          });
          fetchMappings({
            tenantId,
            courier: activeCourier.courier_partner_code,
            isMapped: false,
          });
        } catch (error) {
          message.error("Failed to map the item.");
          console.error("Failed to post mapping:", error);
        }
      }
    } else if (over.id === "unmapped") {
      try {
        await postMapping({
          tenant_id: "traya",
          body: {
            cpCode: activeCourier.courier_partner_code,
            cpStatusCode: draggedKey,
            systemStatusCodeId: null,
          },
        });

        message.success("Unmapping successful!");

        const tenantId = "traya";
        fetchMappings({
          tenantId,
          courier: activeCourier.courier_partner_code,
          isMapped: true,
        });
        fetchMappings({
          tenantId,
          courier: activeCourier.courier_partner_code,
          isMapped: false,
        });
      } catch (error) {
        message.error("Failed to unmap the item.");
        console.error("Failed to unmap:", error);
      }
    }
  };

  if (loading) return <SkeletonLoader />;
  if (error) return <p>Error: {error}</p>;

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#f6f8fa",
        minHeight: "100vh",
      }}
    >
      <Title level={2} style={{ textAlign: "center", marginBottom: "20px" }}>
        Order Tracking Mapper
      </Title>

      <Tabs
        defaultActiveKey={activeCourier?.id}
        activeKey={activeCourier?.id}
        onChange={(id) => {
          const selectedCourier = couriers.find((courier) => courier.id === id);
          setActiveCourier(selectedCourier);
        }}
      >
        {couriers.map((courier) => (
          <TabPane
            tab={
              <span style={{ textTransform: "uppercase" }}>
                {courier?.name}
              </span>
            }
            key={courier?.id}
          >
            <DndContext onDragEnd={handleDragEnd}>
              <div style={{ display: "flex", gap: "20px" }}>
                {/* User-Friendly Keys Section */}
                <div
                  style={{
                    width: "20%",
                    backgroundColor: "#fff",
                    padding: "10px",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    maxHeight: "500px",
                    overflowY: "auto",
                  }}
                >
                  <Title
                    level={4}
                    style={{
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    User-Friendly Keys
                    <Tooltip
                      title="Below are keys that will be shown to customers in the order tracking link."
                      placement="top"
                    >
                      <InfoCircleOutlined
                        style={{
                          marginLeft: "8px",
                          color: "#1890ff",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                      />
                    </Tooltip>
                  </Title>
                  <div>
                    {systemStatusCodes.map((code) => (
                      <div
                        key={code?.id}
                        style={{
                          padding: "10px",
                          margin: "5px 0",
                          backgroundColor:
                            activeUserFriendlyKey === code.system_status_code
                              ? "#e6f7ff"
                              : "#f9f9f9",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setActiveUserFriendlyKey(code?.system_status_code)
                        }
                      >
                        {code.status_description}{" "}
                        <Tooltip
                          title={`Polling time is ${code.next_poll_at} minutes Click to edit`}
                          placement="top"
                        >
                          <Tag
                            icon={<CheckCircleOutlined />}
                            color="blue"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTag("pollTime", code);
                            }}
                          >
                            {code.next_poll_at} min
                          </Tag>
                        </Tooltip>
                        {/* Polling End Tag */}
                        <Tooltip
                          title={`Polling ${
                            code.end_poll ? "is true" : "false"
                          } Click to edit`}
                          placement="top"
                        >
                          <Tag
                            icon={
                              code.end_poll ? (
                                <CheckCircleOutlined />
                              ) : (
                                <CloseCircleOutlined />
                              )
                            }
                            color={code.end_poll ? "green" : "red"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTag("endPoll", code);
                            }}
                          >
                            {code.end_poll ? "T" : "F"}
                          </Tag>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mapped Keys Section */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "#fff",
                    padding: "10px",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    maxHeight: "500px",
                    overflowY: "auto",
                  }}
                >
                  <Title
                    level={4}
                    style={{
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {activeUserFriendlyKey} - Mapped Keys
                    <Tooltip
                      title={`Below are keys mapped to the "${activeUserFriendlyKey}" key.`}
                      placement="top"
                    >
                      <InfoCircleOutlined
                        style={{
                          marginLeft: "8px",
                          color: "#1890ff",
                          cursor: "pointer",
                          fontSize: "16px",
                        }}
                      />
                    </Tooltip>
                  </Title>
                  <DroppableSection
                    id={`mapped-${activeUserFriendlyKey}`}
                    items={statusMappings
                      .filter(
                        (item) =>
                          item.systemStatusCode === activeUserFriendlyKey
                      )
                      .map((mapping) => ({
                        cpStatusCode: mapping.cpStatusCode,
                        cpStatusDescription: mapping.cpStatusDescription,
                      }))}
                  />
                </div>

                {/* Unmapped Keys Section */}
                <div
                  style={{
                    width: "20%",
                    backgroundColor: "#fff",
                    padding: "10px",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                    maxHeight: "500px",
                    overflowY: "auto",
                  }}
                >
                  <Title level={4} style={{ fontSize: "16px" }}>
                    Unmapped Keys
                  </Title>
                  <DroppableSection
                    id="unmapped"
                    items={unmappedKeys.map((item) => ({
                      cpStatusCode: item.cpStatusCode,
                      cpStatusDescription: item.cpStatusDescription,
                    }))}
                  />
                </div>
              </div>
            </DndContext>
          </TabPane>
        ))}
      </Tabs>
      <Modal
        title={`Edit ${
          editingField === "pollTime" ? "Polling Time" : "Polling End"
        }`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSaveChanges}
        okText="Save"
        cancelText="Cancel"
      >
        {editingField === "pollTime" ? (
          <Input
            value={pollTime}
            onChange={(e) => setPollTime(e.target.value)}
            type="number"
            placeholder="Enter polling time in minutes"
          />
        ) : (
          <div>
            <span>Polling End:- </span>
            <Switch
              checked={endPoll}
              onChange={(checked) => setEndPoll(checked)}
              checkedChildren="True"
              unCheckedChildren="False"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

const DroppableSection = ({ id, items }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        padding: "10px",
        backgroundColor: isOver ? "#e6f7ff" : "#fff",
        borderRadius: "8px",
        minHeight: "150px",
        border: isOver ? "2px dashed #1890ff" : "1px solid #ddd",
      }}
    >
      {items.map((item) => (
        <DraggableItem key={item.cpStatusCode} id={item.cpStatusCode}>
          {item.cpStatusDescription}
        </DraggableItem>
      ))}
      {items.length === 0 && (
        <div style={{ textAlign: "center", color: "#aaa" }}>No items</div>
      )}
    </div>
  );
};

const DraggableItem = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });

  const style = {
    padding: "10px",
    margin: "0 0 8px 0",
    backgroundColor: isDragging ? "#bae7ff" : "#f0f0f0",
    borderRadius: "4px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    cursor: "grab",
    zIndex: isDragging ? 999 : "auto",
    position: isDragging ? "absolute" : "relative",
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

export default OTSManagement;
